import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SearchX, Sparkles } from 'lucide-react';
import { useSearch } from '@/hooks/useSearch';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton } from '@/components/ui/Skeleton';
import { SearchBar } from '@/components/search/SearchBar';
import { SearchFilters } from '@/components/search/SearchFilters';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function SearchPage() {
  const { query, filters, results, isLoading, search, updateFilter, clearFilters } = useSearch();

  return (
    <motion.div initial="initial" animate="animate" className="max-w-4xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Search Quotes</h1>
          <p className="text-surface-500">Find quotes by keyword, author, or category</p>
        </div>

        <SearchBar
          value={query}
          onChange={search}
          autoFocus
          className="mb-4"
        />

        <div className="flex items-center justify-between">
          <SearchFilters filters={filters} onUpdateFilter={updateFilter} />
          {(query || Object.keys(filters).length > 0) && (
            <button onClick={clearFilters} className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap ml-4">
              Clear all
            </button>
          )}
        </div>
      </motion.div>

      <div className="mt-8">
        {!query && Object.keys(filters).length === 0 ? (
          <motion.div variants={fadeUp} className="text-center py-16">
            <Sparkles size={48} className="mx-auto text-surface-300 mb-4" />
            <p className="text-xl text-surface-500">Start typing to search quotes</p>
            <p className="text-sm text-surface-400 mt-2">Search by text, author name, or category</p>
          </motion.div>
        ) : isLoading ? (
          <div className="grid md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <QuoteSkeleton key={i} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <motion.div variants={fadeUp} className="text-center py-16">
            <SearchX size={48} className="mx-auto text-surface-300 mb-4" />
            <p className="text-xl text-surface-500">No results found</p>
            <p className="text-sm text-surface-400 mt-2">Try different keywords or filters</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {results.map((quote: any, index: number) => (
              <motion.div
                key={quote.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/quote/${quote.id}`}>
                  <QuoteCard quote={quote} />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
